import React, { useRef, useState, useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import * as THREE from 'three';

gsap.registerPlugin(ScrollTrigger);

const Experience: React.FC = () => {
  const { camera } = useThree();
  const gridRef = useRef<THREE.Mesh>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useGSAP(() => {
    // Initial Camera State - different for mobile to show grid on load
    if (isMobile) {
      camera.position.set(0, 5, 6); // Higher and closer on mobile
      camera.lookAt(0, 0, -10);
    } else {
      camera.position.set(0, 3, 10);
      camera.lookAt(0, 0, -20);
    }

    // --- MASTER FLYOVER ---
    // Continuous forward movement linked to overall scroll
    gsap.to(camera.position, {
      z: isMobile ? -15 : -20,
      y: isMobile ? 8 : 6,
      ease: "none",
      scrollTrigger: {
        trigger: document.body,
        start: "top top",
        end: "bottom bottom",
        scrub: 1
      }
    });

    // --- SECTION SPECIFIC TRANSITIONS ---
    // Each section triggers a specific "thing" for the grid/points

    // 1. Brands: Grid Tilt
    // When brands appear, tilt the grid to show more depth
    ScrollTrigger.create({
      trigger: "#brands-section",
      start: "top bottom",
      end: "bottom top",
      scrub: 1,
      onUpdate: (self) => {
        if (gridRef.current) {
          // Determine direction based on scroll velocity or progress
          // Just mapping progress to rotation
          const progress = self.progress;
          gridRef.current.rotation.z = gsap.utils.interpolate(0, 0.05, progress);
        }
      }
    });


    // 3. Process: Lateral Shift
    // Camera moves slightly sideways to follow the "path"
    gsap.to(camera.position, {
      x: 2,
      ease: "power1.inOut",
      scrollTrigger: {
        trigger: "#process-section",
        start: "top bottom",
        end: "bottom top",
        scrub: 1
      }
    });

    // 4. Newsletter: Horizon Rise
    // The grid tilts up to meet the user
    if (gridRef.current) {
      const baseRotation = isMobile ? -Math.PI / 2.5 : -Math.PI / 2;
      gsap.to(gridRef.current.rotation, {
        x: baseRotation + 0.3, // Tilt up from base
        ease: "none",
        scrollTrigger: {
          trigger: "#newsletter-section",
          start: "top bottom",
          end: "bottom bottom",
          scrub: 1
        }
      });
    }

  }, { dependencies: [camera, isMobile] });


  // Adjusted positions for mobile
  const gridY = isMobile ? 0 : -2;
  const gridRotationX = isMobile ? -Math.PI / 2.5 : -Math.PI / 2;

  return (
    <>
      <ambientLight intensity={0.5} />

      {/* THE GRID */}
      <mesh
        ref={gridRef}
        rotation={[gridRotationX, 0, 0]}
        position={[0, gridY, isMobile ? -5 : 0]}
      >
        <planeGeometry args={[100, 100, 60, 60]} />
        <meshBasicMaterial
          color="#cbd5e1" // Slate-300
          wireframe
          transparent
          opacity={isMobile ? 0.05 : 0.35}
        />
      </mesh>
    </>
  );
};

export default Experience;
